import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { PageLayout } from "../components/PageLayout";
import {
  Mail,
  MessageCircle,
  Send,
  Plus,
  Trash2,
  Edit,
  Users,
  Check,
  Clock,
  Loader2,
  ExternalLink,
  Search,
  X,
  Building2,
  UserCheck,
  Paperclip,
  FileText,
  Upload,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const AdminComunicazioniPage = () => {
  const [activeTab, setActiveTab] = useState("clienti"); // "clienti" or "referenti"
  const [comunicazioni, setComunicazioni] = useState([]);
  const [users, setUsers] = useState([]);
  const [referenti, setReferenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [referentiDialogOpen, setReferentiDialogOpen] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [referenteSearch, setReferenteSearch] = useState("");
  
  const [formData, setFormData] = useState({
    titolo: "",
    oggetto_email: "",
    messaggio_email: "",
    messaggio_whatsapp: "",
    canali: "email",
    destinatari: [],
    stato: "bozza",
    allegato: null,
    allegato_url: ""
  });

  const fileInputRef = useRef(null);

  // Form per referenti
  const [referentiFormData, setReferentiFormData] = useState({
    oggetto: "",
    messaggio: "",
    referenti_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [comRes, usersRes, referentiRes] = await Promise.all([
        axios.get(`${API}/admin/comunicazioni`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referenti`)
      ]);
      setComunicazioni(comRes.data);
      setUsers(usersRes.data.filter(u => u.role !== "admin"));
      setReferenti(referentiRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      titolo: "",
      oggetto_email: "",
      messaggio_email: "",
      messaggio_whatsapp: "",
      canali: "email",
      destinatari: [],
      stato: "bozza"
    });
    setEditingId(null);
    setUserSearch("");
  };

  const resetReferentiForm = () => {
    setReferentiFormData({
      oggetto: "",
      messaggio: "",
      referenti_ids: []
    });
    setReferenteSearch("");
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openReferentiDialog = () => {
    resetReferentiForm();
    setReferentiDialogOpen(true);
  };

  const openEditDialog = (com) => {
    setFormData({
      titolo: com.titolo,
      oggetto_email: com.oggetto_email || "",
      messaggio_email: com.messaggio_email || "",
      messaggio_whatsapp: com.messaggio_whatsapp || "",
      canali: com.canali,
      destinatari: com.destinatari || [],
      stato: com.stato
    });
    setEditingId(com.comunicazione_id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.titolo) {
      toast.error("Inserisci un titolo");
      return;
    }
    if (formData.destinatari.length === 0) {
      toast.error("Seleziona almeno un destinatario");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      let allegato_url = formData.allegato_url;

      // Upload file se presente
      if (formData.allegato) {
        const uploadData = new FormData();
        uploadData.append("file", formData.allegato);
        const uploadRes = await axios.post(`${API}/upload`, uploadData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data" 
          }
        });
        // Rendi l'URL assoluto per le email
        const baseUrl = window.location.origin;
        allegato_url = uploadRes.data.url.startsWith('http') 
          ? uploadRes.data.url 
          : `${baseUrl}${uploadRes.data.url}`;
      }

      const dataToSend = {
        ...formData,
        allegato_url,
        allegato: undefined // Non inviare il file object
      };

      if (editingId) {
        await axios.put(`${API}/admin/comunicazioni/${editingId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Comunicazione aggiornata");
      } else {
        await axios.post(`${API}/admin/comunicazioni`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Comunicazione creata");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa comunicazione?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/comunicazioni/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Comunicazione eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSend = async (id, canale) => {
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/comunicazioni/${id}/invia`,
        { canale },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { risultati, whatsapp_links } = response.data;
      
      if (canale === "email") {
        toast.success(`Email inviate: ${risultati.email_inviate}${risultati.email_errori > 0 ? ` (${risultati.email_errori} errori)` : ''}`);
      } else if (canale === "whatsapp") {
        setWhatsappLinks(whatsapp_links);
        setWhatsappDialogOpen(true);
        toast.success(`${risultati.whatsapp_generati} link WhatsApp generati`);
      } else {
        toast.success(`Email: ${risultati.email_inviate}, WhatsApp: ${risultati.whatsapp_generati}`);
        if (whatsapp_links.length > 0) {
          setWhatsappLinks(whatsapp_links);
          setWhatsappDialogOpen(true);
        }
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  // Invia email ai referenti
  const handleSendToReferenti = async () => {
    if (!referentiFormData.oggetto || !referentiFormData.messaggio) {
      toast.error("Compila oggetto e messaggio");
      return;
    }
    if (referentiFormData.referenti_ids.length === 0) {
      toast.error("Seleziona almeno un referente");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/comunicazioni/referenti`,
        referentiFormData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { risultati } = response.data;
      let message = `Email inviate: ${risultati.email_inviate}`;
      if (risultati.email_errori > 0) message += ` (${risultati.email_errori} errori)`;
      if (risultati.senza_email > 0) message += ` - ${risultati.senza_email} senza email`;
      
      toast.success(message);
      setReferentiDialogOpen(false);
      resetReferentiForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  const toggleDestinatario = (userId) => {
    setFormData(prev => ({
      ...prev,
      destinatari: prev.destinatari.includes(userId)
        ? prev.destinatari.filter(id => id !== userId)
        : [...prev.destinatari, userId]
    }));
  };

  const toggleReferente = (refId) => {
    setReferentiFormData(prev => ({
      ...prev,
      referenti_ids: prev.referenti_ids.includes(refId)
        ? prev.referenti_ids.filter(id => id !== refId)
        : [...prev.referenti_ids, refId]
    }));
  };

  const selectAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      destinatari: users.map(u => u.user_id)
    }));
  };

  const deselectAllUsers = () => {
    setFormData(prev => ({
      ...prev,
      destinatari: []
    }));
  };

  const selectAllReferenti = () => {
    setReferentiFormData(prev => ({
      ...prev,
      referenti_ids: referenti.filter(r => r.email).map(r => r.referente_id)
    }));
  };

  const deselectAllReferenti = () => {
    setReferentiFormData(prev => ({
      ...prev,
      referenti_ids: []
    }));
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.centro_estetico?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredReferenti = referenti.filter(r =>
    r.nome?.toLowerCase().includes(referenteSearch.toLowerCase()) ||
    r.referente_id?.toLowerCase().includes(referenteSearch.toLowerCase()) ||
    r.email?.toLowerCase().includes(referenteSearch.toLowerCase()) ||
    r.ruolo?.toLowerCase().includes(referenteSearch.toLowerCase())
  );

  // Conta referenti con email
  const referentiConEmail = referenti.filter(r => r.email).length;

  if (loading) {
    return (
      <PageLayout title="Comunicazioni">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Comunicazioni">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Comunicazioni</h1>
            <p className="text-muted-foreground">Invia messaggi a Clienti e Referenti</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("clienti")}
            className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === "clienti"
                ? "border-[#B87A8A] text-[#B87A8A]"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Clienti ({users.length})
          </button>
          <button
            onClick={() => setActiveTab("referenti")}
            className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 -mb-px ${
              activeTab === "referenti"
                ? "border-[#B87A8A] text-[#B87A8A]"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Building2 className="w-4 h-4 inline mr-2" />
            Distributori/Agenti ({referenti.length})
          </button>
        </div>

        {/* Tab Content: Clienti */}
        {activeTab === "clienti" && (
          <>
            <div className="flex justify-end">
              <Button onClick={openNewDialog} className="rounded-full bg-[#B87A8A] hover:bg-[#a06978]">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Comunicazione
              </Button>
            </div>

            {/* Lista Comunicazioni */}
            {comunicazioni.length === 0 ? (
              <Card className="rounded-2xl">
                <CardContent className="py-12 text-center">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Nessuna comunicazione creata</p>
                  <Button onClick={openNewDialog} className="mt-4 rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Crea la prima comunicazione
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {comunicazioni.map((com) => (
                  <Card key={com.comunicazione_id} className="rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold text-lg">{com.titolo}</h3>
                            <Badge variant={com.stato === "inviato" ? "default" : "secondary"} className="rounded-full">
                              {com.stato === "inviato" ? (
                                <><Check className="w-3 h-3 mr-1" /> Inviato</>
                              ) : (
                                <><Clock className="w-3 h-3 mr-1" /> Bozza</>
                              )}
                            </Badge>
                            <Badge variant="outline" className="rounded-full">
                              {com.canali === "email" ? <Mail className="w-3 h-3 mr-1" /> : 
                               com.canali === "whatsapp" ? <MessageCircle className="w-3 h-3 mr-1" /> :
                               <><Mail className="w-3 h-3 mr-1" /><MessageCircle className="w-3 h-3 ml-1" /></>}
                              {com.canali === "entrambi" ? "Email + WhatsApp" : com.canali === "email" ? "Email" : "WhatsApp"}
                            </Badge>
                          </div>
                          
                          {com.oggetto_email && (
                            <p className="text-sm text-muted-foreground mb-1">
                              <strong>Oggetto:</strong> {com.oggetto_email}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="w-4 h-4" />
                            <span>{com.destinatari?.length || 0} destinatari</span>
                            {com.data_invio && (
                              <span className="ml-2">• Inviato il {new Date(com.data_invio).toLocaleDateString("it-IT")}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-wrap">
                          {com.stato === "bozza" && (
                            <>
                              {(com.canali === "email" || com.canali === "entrambi") && (
                                <Button
                                  size="sm"
                                  onClick={() => handleSend(com.comunicazione_id, "email")}
                                  disabled={sending}
                                  className="rounded-full bg-blue-600 hover:bg-blue-700"
                                >
                                  <Mail className="w-4 h-4 mr-1" />
                                  Invia Email
                                </Button>
                              )}
                              {(com.canali === "whatsapp" || com.canali === "entrambi") && (
                                <Button
                                  size="sm"
                                  onClick={() => handleSend(com.comunicazione_id, "whatsapp")}
                                  disabled={sending}
                                  className="rounded-full bg-green-600 hover:bg-green-700"
                                >
                                  <MessageCircle className="w-4 h-4 mr-1" />
                                  Invia WhatsApp
                                </Button>
                              )}
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(com)}
                            className="rounded-full"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(com.comunicazione_id)}
                            className="rounded-full text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Tab Content: Referenti */}
        {activeTab === "referenti" && (
          <>
            {/* Info Card */}
            <Card className="rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-800">Invio Email a Distributori e Agenti</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Ogni referente riceverà la propria email individualmente. Nessuno vedrà gli indirizzi degli altri destinatari.
                    </p>
                    <p className="text-sm text-amber-600 mt-2">
                      <strong>{referentiConEmail}</strong> referenti con email configurata su <strong>{referenti.length}</strong> totali
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={openReferentiDialog} className="rounded-full bg-amber-600 hover:bg-amber-700">
                <Mail className="w-4 h-4 mr-2" />
                Invia Email a Referenti
              </Button>
            </div>

            {/* Lista Referenti */}
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-amber-600" />
                  Lista Referenti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Cerca per nome, codice, email o ruolo..."
                    value={referenteSearch}
                    onChange={(e) => setReferenteSearch(e.target.value)}
                    className="pl-9 rounded-xl"
                  />
                </div>
                <div className="max-h-[400px] overflow-y-auto border rounded-xl">
                  {filteredReferenti.map((ref) => (
                    <div
                      key={ref.referente_id}
                      className="p-3 border-b last:border-b-0 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: ref.colore || '#ccc' }}
                        />
                        <div>
                          <p className="font-medium text-sm">{ref.nome}</p>
                          <p className="text-xs text-muted-foreground">
                            {ref.referente_id} • {ref.ruolo}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {ref.email ? (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            {ref.email}
                          </p>
                        ) : (
                          <p className="text-xs text-red-500">Email non configurata</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Dialog Nuova/Modifica Comunicazione Clienti */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#B87A8A]" />
              {editingId ? "Modifica Comunicazione" : "Nuova Comunicazione ai Clienti"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Titolo e Canali */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titolo Comunicazione *</Label>
                <Input
                  value={formData.titolo}
                  onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                  placeholder="es. Promemoria sessione febbraio"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Canali di Invio *</Label>
                <Select value={formData.canali} onValueChange={(v) => setFormData({...formData, canali: v})}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email"><Mail className="w-4 h-4 inline mr-2" />Solo Email</SelectItem>
                    <SelectItem value="whatsapp"><MessageCircle className="w-4 h-4 inline mr-2" />Solo WhatsApp</SelectItem>
                    <SelectItem value="entrambi"><Send className="w-4 h-4 inline mr-2" />Entrambi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Email Section */}
            {(formData.canali === "email" || formData.canali === "entrambi") && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-xl">
                <h3 className="font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  Contenuto Email
                </h3>
                <div className="space-y-2">
                  <Label>Oggetto Email *</Label>
                  <Input
                    value={formData.oggetto_email}
                    onChange={(e) => setFormData({...formData, oggetto_email: e.target.value})}
                    placeholder="es. Promemoria: Sessione in arrivo!"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Messaggio Email *</Label>
                  <Textarea
                    value={formData.messaggio_email}
                    onChange={(e) => setFormData({...formData, messaggio_email: e.target.value})}
                    placeholder="Scrivi il tuo messaggio..."
                    className="rounded-xl min-h-[150px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Il messaggio verrà inserito nel template ODR Academy con l'immagine e il footer.
                  </p>
                </div>

                {/* Allegato */}
                <div className="space-y-2">
                  <Label>Allegato (Dispensa, PDF, Documento)</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setFormData({...formData, allegato: e.target.files[0]});
                      }
                    }}
                  />
                  {formData.allegato || formData.allegato_url ? (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-xl border">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="flex-1 truncate text-sm">
                        {formData.allegato?.name || formData.allegato_url?.split('/').pop()}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({...formData, allegato: null, allegato_url: ""})}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full rounded-xl h-16 border-dashed"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="flex flex-col items-center">
                        <Paperclip className="w-5 h-5 mb-1 text-blue-600" />
                        <span className="text-sm">Clicca per allegare un file</span>
                      </div>
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground">
                    PDF, Word, Excel, PowerPoint, Immagini (max 10MB)
                  </p>
                </div>
              </div>
            )}

            {/* WhatsApp Section */}
            {(formData.canali === "whatsapp" || formData.canali === "entrambi") && (
              <div className="space-y-4 p-4 bg-green-50 rounded-xl">
                <h3 className="font-semibold flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  Contenuto WhatsApp
                </h3>
                <div className="space-y-2">
                  <Label>Messaggio WhatsApp *</Label>
                  <Textarea
                    value={formData.messaggio_whatsapp}
                    onChange={(e) => setFormData({...formData, messaggio_whatsapp: e.target.value})}
                    placeholder="Scrivi il messaggio per WhatsApp..."
                    className="rounded-xl min-h-[100px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Usa {"{nome}"} per inserire il nome del cliente e {"{centro}"} per il nome del centro.
                  </p>
                </div>
              </div>
            )}

            {/* Destinatari */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Destinatari ({formData.destinatari.length} selezionati)</Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={selectAllUsers} className="rounded-full text-xs">
                    Seleziona tutti
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={deselectAllUsers} className="rounded-full text-xs">
                    Deseleziona tutti
                  </Button>
                </div>
              </div>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cerca per nome, email o centro..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 rounded-xl"
                />
              </div>
              
              <div className="max-h-[200px] overflow-y-auto border rounded-xl">
                {filteredUsers.map((user) => (
                  <div
                    key={user.user_id}
                    onClick={() => toggleDestinatario(user.user_id)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-3 ${
                      formData.destinatari.includes(user.user_id) ? "bg-[#B87A8A]/10" : ""
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      formData.destinatari.includes(user.user_id) 
                        ? "bg-[#B87A8A] border-[#B87A8A]" 
                        : "border-gray-300"
                    }`}>
                      {formData.destinatari.includes(user.user_id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    {user.centro_estetico && (
                      <span className="text-xs text-muted-foreground">{user.centro_estetico}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full">
              Annulla
            </Button>
            <Button onClick={handleSave} className="flex-1 rounded-full bg-[#B87A8A] hover:bg-[#a06978]">
              {editingId ? "Salva Modifiche" : "Crea Comunicazione"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Invio Email Referenti */}
      <Dialog open={referentiDialogOpen} onOpenChange={setReferentiDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              Invia Email a Distributori/Agenti
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Info */}
            <div className="p-3 bg-amber-50 rounded-xl text-sm text-amber-800 flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Ogni referente riceverà un'email individuale. Nessuno vedrà gli indirizzi degli altri.</span>
            </div>

            {/* Oggetto */}
            <div className="space-y-2">
              <Label>Oggetto Email *</Label>
              <Input
                value={referentiFormData.oggetto}
                onChange={(e) => setReferentiFormData({...referentiFormData, oggetto: e.target.value})}
                placeholder="es. Aggiornamento ODR Academy"
                className="rounded-xl"
              />
            </div>

            {/* Messaggio */}
            <div className="space-y-2">
              <Label>Messaggio *</Label>
              <Textarea
                value={referentiFormData.messaggio}
                onChange={(e) => setReferentiFormData({...referentiFormData, messaggio: e.target.value})}
                placeholder="Scrivi il tuo messaggio per i referenti..."
                className="rounded-xl min-h-[150px]"
              />
              <p className="text-xs text-muted-foreground">
                Il messaggio verrà inserito nel template ODR Academy con il nome del referente.
              </p>
            </div>

            {/* Selezione Referenti */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">
                  Destinatari ({referentiFormData.referenti_ids.length} selezionati)
                </Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={selectAllReferenti} className="rounded-full text-xs">
                    Seleziona tutti
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={deselectAllReferenti} className="rounded-full text-xs">
                    Deseleziona tutti
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Cerca per nome, codice o ruolo..."
                  value={referenteSearch}
                  onChange={(e) => setReferenteSearch(e.target.value)}
                  className="pl-9 rounded-xl"
                />
              </div>

              <div className="max-h-[250px] overflow-y-auto border rounded-xl">
                {filteredReferenti.filter(r => r.email).map((ref) => (
                  <div
                    key={ref.referente_id}
                    onClick={() => toggleReferente(ref.referente_id)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-3 ${
                      referentiFormData.referenti_ids.includes(ref.referente_id) ? "bg-amber-50" : ""
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      referentiFormData.referenti_ids.includes(ref.referente_id) 
                        ? "bg-amber-600 border-amber-600" 
                        : "border-gray-300"
                    }`}>
                      {referentiFormData.referenti_ids.includes(ref.referente_id) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: ref.colore || '#ccc' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{ref.nome}</p>
                      <p className="text-xs text-muted-foreground truncate">{ref.email}</p>
                    </div>
                    <Badge variant="outline" className="rounded-full text-xs">
                      {ref.ruolo}
                    </Badge>
                  </div>
                ))}
                {filteredReferenti.filter(r => !r.email).length > 0 && (
                  <div className="p-3 bg-gray-50 text-center text-xs text-muted-foreground">
                    {filteredReferenti.filter(r => !r.email).length} referenti nascosti (senza email)
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setReferentiDialogOpen(false)} className="flex-1 rounded-full">
              Annulla
            </Button>
            <Button 
              onClick={handleSendToReferenti} 
              disabled={sending}
              className="flex-1 rounded-full bg-amber-600 hover:bg-amber-700"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio in corso...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Invia Email</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog WhatsApp Links */}
      <Dialog open={whatsappDialogOpen} onOpenChange={setWhatsappDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              Link WhatsApp Generati
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2 max-h-[400px] overflow-y-auto py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Clicca su ogni link per aprire WhatsApp e inviare il messaggio:
            </p>
            {whatsappLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
              >
                <div>
                  <p className="font-medium text-sm">{link.name}</p>
                  <p className="text-xs text-muted-foreground">{link.phone}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-green-600" />
              </a>
            ))}
          </div>
          
          <Button onClick={() => setWhatsappDialogOpen(false)} className="w-full rounded-full">
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default AdminComunicazioniPage;
